import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Save,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { VENDOR_FIELDS, FIELD_GROUPS } from "@/lib/fields";
import { cellStatus, normalizeValue } from "@/lib/validation";
import { cn } from "@/lib/utils";

const EXTRA = [
  { key: "amount", label: "Amount (₹)", group: "Payment" },
  { key: "currency", label: "Currency", group: "Payment" },
  { key: "approver_email", label: "Approver email", group: "Payment" },
];

export default function QueueRowDialog({ row, open, onOpenChange, onSave, saving, requiredFields = [] }) {
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (row) setDraft({ ...row });
  }, [row]);

  const set = (key, value) => {
    setDraft((p) => ({ ...p, [key]: key === "amount" ? value : normalizeValue(key, value) }));
  };

  const groups = useMemo(() => {
    const all = [...VENDOR_FIELDS.map((f) => ({ ...f })), ...EXTRA];
    return [...FIELD_GROUPS, "Payment"]
      .map((g) => ({ group: g, fields: all.filter((f) => f.group === g) }))
      .filter((g) => g.fields.length);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-3xl overflow-y-auto bg-card"
        data-testid="queue-row-dialog"
      >
        <DialogHeader>
          <DialogTitle>Edit all vendor fields</DialogTitle>
          <DialogDescription>
            {row?.source === "gmail" ? "Extracted from a Gmail thread. " : "Manually added. "}
            PAN, GSTIN and IFSC auto-uppercase; status recalculates from your Required fields
            configuration.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {groups.map(({ group, fields }) => (
            <div key={group}>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {group}
              </p>
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {fields.map((f) => {
                  const value = draft?.[f.key] ?? "";
                  const st = f.key === "amount" || f.key === "currency" || f.key === "approver_email"
                    ? { level: "ok" }
                    : cellStatus(f.key, value);
                  const isReq = requiredFields.includes(f.key);
                  const empty = value === "" || value === null || value === undefined;
                  return (
                    <div key={f.key} className={cn(f.long && "sm:col-span-2")}>
                      <Label className="mb-1 flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
                        {f.label}
                        {isReq ? (
                          <span className="text-danger-text" title="Required field">
                            *
                          </span>
                        ) : null}
                      </Label>
                      {f.long ? (
                        <Textarea
                          rows={3}
                          value={value}
                          onChange={(e) => set(f.key, e.target.value)}
                          className="bg-card text-[13px]"
                          data-testid={`dlg-input-${f.key}`}
                        />
                      ) : (
                        <Input
                          value={value}
                          onChange={(e) => set(f.key, e.target.value)}
                          className={cn(
                            "bg-card text-[13px]",
                            f.mono && "vde-code",
                            st.level === "error" && "border-danger-border bg-danger-surface",
                            st.level === "warn" && "border-warning-border bg-warning-surface",
                            isReq && empty && "border-warning-border bg-warning-surface"
                          )}
                          data-testid={`dlg-input-${f.key}`}
                        />
                      )}
                      {st.message ? (
                        <p
                          className={cn(
                            "mt-1 flex items-start gap-1 text-[10px]",
                            st.level === "error" ? "text-danger-text" : "text-warning-text"
                          )}
                        >
                          <AlertTriangle className="mt-px h-2.5 w-2.5 shrink-0" aria-hidden="true" />
                          {st.message}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dlg-cancel-button">
            <X className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" /> Cancel
          </Button>
          <Button onClick={() => onSave(draft)} disabled={saving} data-testid="dlg-save-button">
            {saving ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            )}
            Save row
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
